<script setup lang="ts">
import { computed, ref, inject, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { QField } from 'quasar';
import type { Size } from 'vue-advanced-cropper';

import UploadImageDialog from './UploadImageDialog.vue';
import ColorPicker from './ColorPicker.vue';
import RoundLogoImage from './RoundLogoImage.vue';
import LocalizedInput from './LocalizedInput.vue';

import { formSaveImageGuard } from '@/helpers/form.helper';
import { getImageUrl } from '@/helpers/image.helper';
import type { Locales } from '@/services/types/api.types';
import type { FileImageType, FileInfo, FileVersion } from '@/services/types/file.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import type { ImageType } from './base/BaseImageUpload.vue';

export interface CroppingInfo {
  aspectRatio: number;
  size: Size;
}

const props = withDefaults(
  defineProps<{
    source?: string | null;
    label: string;
    targetWidth?: number;
    targetHeight?: number;
    onClearImage: () => Promise<void>;
    onSaveImage: (options: SavingOptions) => Promise<void>;
    formDirty: boolean;
    colorPicker?: boolean;
    backgroundColor?: string;
    altTextName?: string | undefined;
    languageEnabled: Locales[];
    type?: ImageType;
    version?: FileVersion;
    error?: boolean;
    errorMessage?: string;
    file?: FileInfo | null;
    cropping?: CroppingInfo;
  }>(),
  { colorPicker: false, backgroundColor: '#ffffff', type: 'community', version: 'v1', error: false },
);

const { t } = useI18n();

const editLocale = inject<Ref<Locales>>('editLocale');

const uploadImageDialogRef = ref<InstanceType<typeof UploadImageDialog>>();
const fieldRef = ref<InstanceType<typeof QField>>();
const openModal = ref(false);

const imageSource = computed(() => {
  const url = props.version === 'v2' ? getImageUrl(props.file) : props.source;
  const size = props.cropping?.size ? props.cropping.size : { width: props.targetWidth!, height: props.targetHeight! };
  return url ?? `https://placehold.co/${size.width}x${size.height}`;
});
const hasImage = computed(() => (props.version === 'v2' ? !!props.file?.url : !!props.source));
const aspectRatio = computed(() => (props.targetWidth ?? 1) / (props.targetHeight ?? 1));
const fileImageType = computed<FileImageType>(() => props.file?.metadata?.image?.type ?? 'raw');

const openDialog = (mode: 'add' | 'edit') => {
  if (uploadImageDialogRef.value) {
    uploadImageDialogRef.value.openDialog(mode);
  }
};
const handleUpload = () => {
  formSaveImageGuard(props.formDirty, () => openDialog('add'));
};

const handleEdit = () => {
  formSaveImageGuard(props.formDirty, () => openDialog('edit'));
};

const handleZoomIn = () => {
  fieldRef.value?.blur();
  openModal.value = true;
};

const handleDelete = async () => {
  await props.onClearImage();
};

const closeModal = () => {
  openModal.value = false;
};
</script>
<template>
  <div class="image-field" :class="{ 'image-field--error': props.error }">
    <div class="image-field__title">
      {{ props.label }}
    </div>
    <div class="image-field__image">
      <div v-if="props.label === t('community.image.logo.label')">
        <round-logo-image :source="imageSource" :color="props.backgroundColor" :alt-text="props.label" />
      </div>
      <div v-else class="image-field__image-container">
        <img :src="imageSource" :alt="props.label" />
      </div>
      <div class="image-field__buttons-container">
        <q-btn flat icon="o_zoom_in" color="primary" @click="handleZoomIn" :disable="!hasImage" />
        <q-btn flat icon="o_add_a_photo" color="primary" @click="handleUpload" />
        <q-btn flat icon="o_edit" color="primary" @click="handleEdit" :disable="fileImageType !== 'crop'" />
        <q-btn flat icon="o_delete" color="primary" @click="handleDelete" :disable="!hasImage" />
      </div>
    </div>
    <div v-if="props.colorPicker" class="image-field__background-color">
      <color-picker :color="props.backgroundColor" :label="t('global.label.background-color')" />
    </div>
    <div class="image-field__alt-text" v-if="props.altTextName && hasImage">
      <localized-input
        :name="props.altTextName"
        :label="t('global.label.alt-text')"
        :language_enabled="props.languageEnabled"
        :editLocale="editLocale"
        outlined
      />
    </div>
  </div>
  <div v-if="props.error && props.errorMessage" class="image-field__error">
    {{ props.errorMessage }}
  </div>
  <upload-image-dialog
    ref="uploadImageDialogRef"
    :title="props.label"
    :type="props.type"
    :aspect-ratio="aspectRatio"
    :on-save-image="onSaveImage"
    :version="props.version"
    :cropping="props.cropping"
    :file="props.file"
  />
  <q-dialog v-model="openModal">
    <q-card flat style="width: auto; max-width: 90%; max-height: 90%">
      <img :src="imageSource" @click="closeModal" />
    </q-card>
  </q-dialog>
</template>

<style lang="scss">
.image-field {
  border: 1px solid rgba(0, 0, 0, 0.24);
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  padding: 8px 12px 16px 12px;
  border-radius: 4px;

  &--error {
    border-color: var(--q-negative);
    outline: 1px solid var(--q-negative);

    .image-field__title {
      color: var(--q-negative);
    }
  }

  &__error {
    margin-top: -12px;
    font-size: 12px;
    color: var(--q-negative);
    line-height: 1.2;
  }

  &__title {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 4px;
  }

  &__image {
    display: flex;
    flex-direction: row;

    &-container {
      display: flex;
      padding: 8px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 10px;
      background: #fff;
      box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25);

      img {
        height: 112px;
        width: 100%;
      }
    }
  }

  &__buttons-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: stretch;
    padding-left: 8px;
    .q-btn .q-icon {
      font-size: 20px;
    }
  }

  &__background-color {
    display: flex;
    padding: 20px 0 0 0;
  }

  &__alt-text {
    margin-top: 16px;
  }
}
</style>
